// Offline-logout stash re-inject — re-queue the SYNCABLE dimension into the LIVE
// capture path (#809 Tier-3 follow-up).
//
// The earlier Tier-3 re-inject shipped ALL stashed offline counts to the isolated
// server recovery inbox (POST /capture/recovery-dump), where they sat un-placed and
// INVISIBLE to the surveyor — logging back in never brought the counts back. This
// module restores the ps_crud-QUEUED dimension the way an offline capture is meant
// to come back: by re-INSERTing each count into the local `capture_items` table.
// PowerSync queues a PUT CRUD op for that INSERT exactly as the live capture write
// does (capture-client-powersync-create-item.ts), the connector uploads it on the
// next sync, and the surveyor watches the upload queue DRAIN on the device — the
// counts become live `capture_items` (dashboard / CSV), not inbox rows.
//
// IDEMPOTENT + NEVER-LOSE. The original capture_items id (the stash's
// `originalItemId`) is reused, so:
//   - a row that synced down again before this runs — a count that reached the server
//     before logout, OR the synced-down row a stashed PATCH-only EDIT targets (rescue-
//     held-captures.ts routes live edits down this path) — collides on the local PRIMARY
//     KEY. Caught: if the stashed value DIFFERS it is re-applied as a sync_version-bumped
//     PATCH (:155); otherwise treated as already-present. Never a hard failure;
//   - the server resolves a same-id re-create idempotently (create-item.ts
//     tryIdempotentReturn → 200), so a re-queued count never duplicates.
// A genuine write failure (NOT a PK collision) is RETHROWN so the caller leaves the
// count in the stash to retry next login — the count is never silently dropped.
//
// SELF-HEALING for the rare structurally-broken count: if a re-queued count still
// can't sync (its room/session was deleted server-side), the connector quarantines
// it (connector-dispatch-create-item.ts: 404 / invalid_argument → quarantine, NOT a
// queue wedge) and the live AutoRescueHeldCaptures ships it to the recovery inbox —
// the same place the quarantine dimension goes directly. So re-queuing is safe.
//
// TOMBSTONE-SAFE ON BOTH UPLOAD PATHS (a re-queued count whose id is a server-side
// admin-rejected tombstone): a CREATE-PUT is caught by the backend's
// assertNoRejectedIdCollision (create-item.ts → notFound → quarantine); an edit's
// PATCH→PUT is caught independently by update-item.ts's `rejectedAt IS NULL`-filtered
// load (→ notFound → quarantine). Neither 23505-wedges nor resurrects a rejected count.
//
// OFFLINE-STARTED SESSIONS (Codex P1 residual, documented). The stash carries the
// counts only, not the parent capture_sessions PUT. If the session itself was created
// offline and wiped by the clear, a re-queued count references a server session that
// doesn't exist yet → the connector's session-recovery (recoverSessionForItem →
// loadSessionIdForBuilding) re-homes it onto the building's LIVE session and it syncs;
// only when the building's first-ever session was offline (no live session to re-home
// onto) does it 404 → quarantine → recovery inbox (preserved, never lost — it just
// doesn't sync live). Restoring an offline-created session tree is the separate
// offline-session-create family's scope, not this fix.
//
// Pure over a minimal write seam so it is unit-testable with a fake db (no
// PowerSync singleton import — the wiring lives in logout-cleanup.ts).

import {
  MAX_FALLBACK_ID_GENERATIONS,
  mintFallbackCaptureItemId,
} from '@/capture/client/capture-item-id'

import { quantitiesEqual } from './connector-quantity-equal'
import { canonicalizeQuantity } from './quantity-canonical'
import type { RecoveryDumpItem } from './rescue-held-captures'

/** The minimal local-SQLite surface the re-queue needs (read + write). `getAll` is
 *  non-generic (the only read is a `SELECT 1`-style collision probe) so the
 *  PowerSync singleton adapts with a plain forward, no cast — mirroring ReadableDb. */
export interface CaptureItemsWriteDb {
  execute(sql: string, params?: unknown[]): Promise<unknown>
  getAll(sql: string, params?: unknown[]): Promise<Record<string, unknown>[]>
}

/**
 * Re-queue each stashed `queueItems` count into the live capture path. Resolves
 * with the number successfully (re)queued — both a fresh INSERT and an idempotent
 * already-present row count as handled, so the caller removes them from the stash.
 * A genuine (non-collision) write error THROWS so the caller retries it next login.
 */
export async function requeueStashedCounts(
  db: CaptureItemsWriteDb,
  items: RecoveryDumpItem[],
): Promise<number> {
  let requeued = 0
  for (const item of items) {
    if (await requeueOne(db, item)) requeued += 1
  }
  return requeued
}

async function requeueOne(
  db: CaptureItemsWriteDb,
  item: RecoveryDumpItem,
): Promise<boolean> {
  // A stash item with no id can never be a capture_items row — skip it (the inbox
  // dimension keeps such forensic rows; the re-queue path needs a real PK).
  if (!item.originalItemId) return false

  // Preserve the FIELD-TIME capture instant (H21 #710): re-stamping captured_at to
  // re-login time would corrupt CSV column 16, the dashboard timeseries, and the
  // export's captured_at ORDER BY. Fall back to SQLite now() only when the stash
  // genuinely lacks one. COALESCE(?, now()) keeps the null→now() default in SQL.
  const capturedAt =
    typeof item.capturedAt === 'string' && item.capturedAt !== ''
      ? item.capturedAt
      : null

  // Canonicalise the quantity to the same shape the live create path persists
  // (canonicalizeQuantity(raw) ?? raw — identical to capture-client-powersync-
  // create-item.ts). The stash quantity originates from a DB capture_items row, so
  // it is already canonical; the `?? raw` forwards an un-repairable value UNCHANGED
  // rather than silently altering the contractual count (the connector's upload POST
  // then re-canonicalises, and the backend — never this layer — rejects a bad one).
  const rawQuantity = typeof item.quantity === 'string' ? item.quantity : ''
  const quantity = canonicalizeQuantity(rawQuantity) ?? rawQuantity

  const isZeroConfirmed = item.isZeroConfirmed === 1 ? 1 : 0

  // Rejected-tombstone guard (mirrors capture-client-powersync-create-item.ts; Codex
  // P1). A count can stay in ps_crud after reaching the server (uploaded but the batch
  // wasn't acked); an admin can then soft-reject it, and the captures stream syncs the
  // same id back down with `rejected_at` set. Re-using that id would (a) PK-collide on
  // the local view and (b) be mistaken for an idempotent success by the collision probe
  // below — silently DROPPING the field's count with no live row and no queued PUT. So
  // detect the tombstone and mint a FRESH id: the re-queued count becomes a NEW live row
  // coexisting with the tombstone (the admin reject stands; the count is preserved). The
  // `let id` assignment (not a direct param) is what the deterministic-id gate's reuse
  // arm anchors on — the id is still the persisted deterministic id, never a random one.
  let id = item.originalItemId
  // Walk fallback generations (see capture-client-powersync-create-item.ts): the id may
  // be a rejected tombstone; mint a DETERMINISTIC fallback seeded by it (a retry of this
  // same tombstoned item — drainDimension re-runs the whole chunk if a sibling threw —
  // mints the SAME id, colliding idempotently, no DUPLICATE live count). If THAT fallback
  // is also a tombstone (rejected → re-added → rejected again), mint the next generation,
  // until the candidate is live — keeping a twice-rejected slot re-addable (Codex).
  // Bounded by MAX_FALLBACK_ID_GENERATIONS so a reject history can't loop forever.
  for (let gen = 0; gen < MAX_FALLBACK_ID_GENERATIONS; gen += 1) {
    const tombstone = await db.getAll(
      `SELECT 1 AS hit FROM capture_items WHERE id = ? AND rejected_at IS NOT NULL LIMIT 1`,
      [id],
    )
    if (tombstone.length === 0) break
    id = await mintFallbackCaptureItemId(id)
  }
  // RESURRECT-vs-INBOX (Codex): minting a fresh id above re-creates a SOFT-rejected count
  // as a NEW live row rather than routing it to the recovery inbox. This is deliberate and
  // create-path-consistent (capture-client-powersync-create-item.ts does the same on a
  // tombstone collision), and now idempotent (deterministic id, so no duplicate on retry).
  // Whether a late field count should survive an admin reject — resurrect vs inbox — is the
  // SAME AE4 product call as the hard-delete case below (Rohan, ADR-022); the live create
  // path already resurrects, so this mirrors it rather than unilaterally diverging.
  // ASYMMETRY (known, narrow residual): this probe catches a SOFT-reject tombstone
  // (rejected_at set). An admin HARD-delete (ADR-017) leaves NO capture_items row, so the
  // probe misses and the re-INSERT below lands under the original id — i.e. a re-queued
  // count whose slot was hard-deleted while the user was offline RESURRECTS it. This is
  // pre-existing for fresh creates (the live create path has always behaved this way) and
  // is consistent with ADR-022 OQ-1 (a late/offline field count LANDS — "never silently
  // drop a count" wins; the field reopen re-locks the admin). It is a resurrection, not a
  // loss, behind an audited admin action in a narrow window (hard-delete is AE4-gated on
  // building=complete, then a field reopen). Honoring an admin hard-delete OVER a late
  // field count is an AE4 product call (Rohan, ADR-022), not changed here.

  try {
    await db.execute(
      `INSERT INTO capture_items (
         id, capture_session_id, site_id, building_id, space_id,
         asset_type_id, quantity, is_zero_confirmed, notes,
         captured_at, updated_at, sync_version
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?,
                COALESCE(?, strftime('%Y-%m-%dT%H:%M:%fZ','now')),
                strftime('%Y-%m-%dT%H:%M:%fZ','now'),
                1)`,
      [
        id,
        item.captureSessionId,
        item.siteId,
        item.buildingId,
        item.spaceId,
        item.assetTypeId,
        quantity,
        isZeroConfirmed,
        item.notes,
        capturedAt,
      ],
    )
    return true
  } catch (err) {
    // A same-id LIVE row already exists locally (PK collision). `rejected_at IS NULL`
    // — a tombstone was handled above by minting a fresh id, so any collision here is a
    // live row. ON CONFLICT is unsupported on PowerSync views, so we detect the
    // collision by reading the row rather than UPSERTing.
    const live = await db.getAll(
      `SELECT quantity, is_zero_confirmed, notes, sync_version
         FROM capture_items WHERE id = ? AND rejected_at IS NULL LIMIT 1`,
      [id],
    )
    const liveRow = live[0]
    if (liveRow !== undefined) {
      // :155 (Codex) — the live row can be STALE relative to the stashed FINAL value.
      // Two ways this happens: (a) a partial upload landed a create's INITIAL quantity
      // but the later offline PATCHes never uploaded, so the server row synced back at
      // the initial value; (b) this stash item IS a PATCH-only edit to an already-synced
      // row (rescue-held-captures.ts routes live edits down the re-queue path), and the
      // synced-down row still holds the pre-edit value. Acking here would silently DROP
      // the edit. So if the stashed count differs on ANY meaningful field — quantity,
      // zero-confirmation, OR notes — RE-APPLY it via an UPDATE (→ ps_crud PATCH → the
      // connector PUTs the final value under LWW, ADR-003) instead of dropping it.
      // Quantity matches by VALUE (quantitiesEqual handles '5.00' vs '5'); notes match
      // exactly with null-coercion. The UPDATE fires only on a real difference so
      // PowerSync still emits a sync_version-bearing PATCH (an unchanged UPDATE would
      // drop sync_version → invalid_argument).
      const sameQuantity = quantitiesEqual(liveRow['quantity'], quantity)
      const sameZero =
        (liveRow['is_zero_confirmed'] === 1 ? 1 : 0) === isZeroConfirmed
      const sameNotes =
        (typeof liveRow['notes'] === 'string' ? liveRow['notes'] : null) ===
        (typeof item.notes === 'string' ? item.notes : null)
      // A NOTES-only re-apply on a CONFIRMED-ZERO row is now SAFE to fire. The earlier
      // `notesOnlyOnConfirmedZero` suppression worked around a connector trim-gap: the
      // resulting version-bearing PATCH trims the UNCHANGED is_zero_confirmed, and the
      // connector's recovery used to be gated on a MISSING sync_version, so the upload
      // would have sent is_zero_confirmed=false and CLEARED the contractual flag
      // (ADR-021). The connector now recovers a trimmed is_zero_confirmed / notes from
      // the local row for a VERSION-BEARING PATCH too (connector-dispatch-item-patch.ts),
      // so the flag survives — and the surveyor's note is no longer dropped. Re-apply on
      // ANY real diff.
      if (!sameQuantity || !sameZero || !sameNotes) {
        const liveVersion =
          typeof liveRow['sync_version'] === 'number' ? liveRow['sync_version'] : 1
        await db.execute(
          `UPDATE capture_items
             SET quantity = ?, is_zero_confirmed = ?, notes = ?, sync_version = ?,
                 updated_at = strftime('%Y-%m-%dT%H:%M:%fZ','now')
           WHERE id = ?`,
          [quantity, isZeroConfirmed, item.notes, liveVersion + 1, id],
        )
      }
      return true
    }
    // No live row ⇒ a genuine write failure (DB locked / wedged worker). Never silently
    // drop: rethrow so the caller leaves the count in the stash to retry next login.
    throw err
  }
}
